"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  numeral: string;
  question: string;
  answer: string;
}
interface FaqCategory {
  category: string;
  items: FaqItem[];
}
const faqCategories: FaqCategory[] = [{
  category: "Daily Logistics",
  items: [{
    numeral: "I.",
    question: "How do we handle morning drop-offs?",
    answer: "Morning drop-offs occur between 8:15 AM and 8:30 AM at the main gate. Please ensure your child has their backpack and water bottle ready. Our classroom assistants will be waiting to greet every student with a smile and help them settle into their morning activity stations."
  }, {
    numeral: "II.",
    question: "What is the policy for lunch and snacks?",
    answer: "We encourage healthy, nut-free snacks and lunches. Please label all containers clearly with your child's name. If your child forgets their lunch, we have a small supply of emergency healthy snacks available in the classroom pantry to ensure no student goes hungry."
  }, {
    numeral: "III.",
    question: "How are absences reported?",
    answer: "If your child is unwell or unable to attend, please notify the school office by 8:00 AM. You can also send a quick message through our parent portal. We appreciate early notice so we can adjust our daily lesson plans and group activities accordingly."
  }]
}, {
  category: "Classroom Policies",
  items: [{
    numeral: "IV.",
    question: "What is our behavior philosophy?",
    answer: "In Class 2A, we focus on positive reinforcement and 'kindness circles.' We teach students to express their feelings using 'I' statements and encourage collaborative problem-solving. Our goal is to create a safe, inclusive environment where every child feels heard and respected."
  }, {
    numeral: "V.",
    question: "How do we handle lost items?",
    answer: "We maintain a 'Lost & Found' bin inside the classroom cubby area. We strongly recommend labeling all jackets, sweaters, and lunchboxes with permanent markers. Items not claimed by the end of each month are donated to the school charity drive."
  }]
}, {
  category: "Learning & Growth",
  items: [{
    numeral: "VI.",
    question: "How long is the daily reading time?",
    answer: "We dedicate 30 minutes every afternoon to 'Quiet Reading Corner' time. Students are encouraged to choose books from our classroom library that match their interests. We believe that fostering a love for reading early on is the foundation for all future academic success."
  }, {
    numeral: "VII.",
    question: "Can we volunteer in the classroom?",
    answer: "We love having parent volunteers! Whether it's helping with art projects, reading stories, or assisting during science experiments, your presence is always welcome. Please check the sign-up sheet on the classroom door to see available slots for the upcoming month."
  }, {
    numeral: "VIII.",
    question: "How are progress updates shared?",
    answer: "We send home a 'Weekly Wonder' newsletter every Friday, highlighting the projects we worked on and upcoming milestones. Additionally, we hold parent-teacher conferences twice a year to discuss your child's individual growth, social development, and academic achievements."
  }]
}, {
  category: "School Life",
  items: [{
    numeral: "IX.",
    question: "How is homework structured?",
    answer: "Homework in Class 2A is designed to be light and engaging, focusing on reinforcing concepts learned in class. We provide a weekly packet that includes a reading log and a small math challenge. It should take no more than 15-20 minutes to complete each evening."
  }, {
    numeral: "X.",
    question: "Do you offer payment plans for trips?",
    answer: "For larger field trips, we offer a flexible payment schedule to accommodate family budgets. Please reach out to the school administration office if you need to discuss a payment plan or if you require financial assistance for any classroom activity."
  }, {
    numeral: "XI.",
    question: "What is the classroom safety policy?",
    answer: "Safety is our top priority. All classroom visitors must sign in at the main office and wear a visitor badge. Our classroom is equipped with a first-aid kit, and all staff members are trained in basic pediatric first aid and emergency response protocols."
  }]
}];
export default function ImportantReminders() {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const accordion = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const closing = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const toggleItem = (key: string) => {
    setOpenItem(prev => prev === key ? null : key);
  };
  return <section data-section-id="3029" id="important-reminders" className="bg-background text-foreground py-24 md:py-32 lg:py-[180px] px-6 md:px-10">
      <div className="max-w-[1200px] mx-auto">
        {}
        <div ref={intro.ref} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="mx-auto mb-8 text-[var(--primary)] text-2xl" aria-hidden="true" style={{
          fontFamily: "var(--font-serif)"
        }}>
            &#10038;
          </div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--primary)] mb-10">
            &mdash; Important Reminders
          </p>
          <h2 className="font-default font-light text-4xl md:text-5xl lg:text-[72px] leading-[1.05] text-foreground mb-10">
            Classroom
            <br />
            guidelines,
            <br />
            <span className="italic text-[var(--primary)]">made simple.</span>
          </h2>
          <p className="font-sans text-base leading-[1.8] text-muted-foreground max-w-[580px] mx-auto">
            Everything you need to know to help your child thrive in Class 2A. From daily routines to school policies, we have gathered the most common questions here for your convenience.
          </p>
        </div>

        {}
        <div ref={accordion.ref} className={`max-w-[880px] mx-auto transition-all duration-1000 ease-out delay-150 ${accordion.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {faqCategories.map((cat, ci) => <div data-index={ci} key={cat.category} className="mb-14 last:mb-0">
              <h3 className="font-default italic text-[var(--primary)] text-sm md:text-base tracking-[0.15em] uppercase mb-4 pl-2" style={{
            letterSpacing: "0.2em"
          }}>
                {cat.category}
              </h3>

              <div>
                {cat.items.map((item, ii) => {
              const key = `${ci}-${ii}`;
              const isOpen = openItem === key;
              return <div data-index={ii} key={key} className="border-t border-[var(--primary)]/40 last:border-b last:border-b-[var(--primary)]/40">
                      <Button onClick={() => toggleItem(key)} variant="ghost" className="group w-full justify-start gap-6 md:gap-8 py-7 md:py-8 h-auto px-0 hover:bg-transparent whitespace-normal">
                        <span className="font-default italic text-[var(--primary)] text-base md:text-lg pt-1 w-8 shrink-0" aria-hidden="true">
                          {item.numeral}
                        </span>
                        <span className={`flex-1 text-left font-default font-normal text-foreground text-xl md:text-2xl leading-[1.35] transition-all duration-300 ${isOpen ? "italic" : "group-hover:italic"}`}>
                          {item.question}
                        </span>
                        <span className="shrink-0 pt-1 text-[var(--primary)]" aria-hidden="true">
                          <Plus className={`size-7 md:size-8 transition-transform duration-[400ms] ease-out ${isOpen ? "rotate-45" : "rotate-0"}`} strokeWidth={1} />
                        </span>
                      </Button>

                      <div className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                        <div className="overflow-hidden">
                          <div className="pl-14 md:pl-16 pr-8 md:pr-16 pb-8">
                            <p className="font-sans text-base text-muted-foreground leading-[1.8]">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>)}
        </div>

        {}
        <div ref={closing.ref} className={`text-center mt-24 md:mt-32 transition-all duration-1000 ease-out delay-200 ${closing.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default italic text-foreground text-[22px] md:text-[26px] leading-snug mb-8">
            Still have a question?
          </p>
          <Button asChild variant="link" className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--primary)] hover:text-[var(--primary)]/80 px-0 h-auto">
            <Link to="mailto:teacher@class2a.edu">
              Email the Teacher Directly &rarr;
            </Link>
          </Button>
          <div className="mt-10 flex justify-center">
            <span className="block w-px h-16 bg-[var(--primary)]/40" aria-hidden="true"></span>
          </div>
        </div>
      </div>
    </section>;
}